import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const Ctx = createContext(null);

/**
 * Holds public page CMS content for `home`, `about`, `contact`.
 * Pages are fetched lazily on demand and cached for the session.
 * 404 (draft / missing) returns null; consumers should fall back to in-code copy.
 */
export function PagesProvider({ children }) {
  const [pages, setPages] = useState({}); // { slug: page | null }

  const fetchPage = useCallback(async (slug) => {
    try {
      const r = await api.get(`/pages/${slug}`);
      setPages((p) => ({ ...p, [slug]: r.data }));
      return r.data;
    } catch {
      setPages((p) => ({ ...p, [slug]: null }));
      return null;
    }
  }, []);

  return (
    <Ctx.Provider value={{ pages, fetchPage, setPages }}>
      {children}
    </Ctx.Provider>
  );
}

export function usePage(slug) {
  const ctx = useContext(Ctx);
  useEffect(() => {
    if (ctx && !(slug in ctx.pages)) {
      ctx.fetchPage(slug);
    }
  }, [ctx, slug]);
  const page = ctx?.pages?.[slug];
  const content = page?.content || {};
  /** copy(key, fallback) — read field with fallback to in-code default */
  const copy = (key, fallback = "") => (content[key] !== undefined && content[key] !== "" ? content[key] : fallback);
  return { page, content, seo: page?.seo || {}, copy, loaded: page !== undefined };
}
